import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Select, Store } from "@ngxs/store";
import { Observable, Subject } from "rxjs";
import { AdvancedReportFormState, AdvancedReportFormStateModel, InitNewReportForm, SubmitReportForm } from "./advanced-report-form.state";
import { AdvancedReport } from "@core/data/types/advanced-report";
import { Status } from "@core/data";
import { takeUntil } from "rxjs/operators";
import { Snackbar } from "@core/material";

export interface ReportFormDialogData {
    key?: string;
    defaults?: Partial<AdvancedReport>;
}

@Component({
    selector: 'pv-advanced-report-form-dialog',
    templateUrl: 'advanced-report-form.dialog.html',
    styleUrls:   ['advanced-report-form.dialog.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    host: {
        class: 'pv-advanced-report-form-dialog'
    },
})
export class AdvancedReportFormDialog implements OnInit, OnDestroy {

    @Select(AdvancedReportFormState)
    state$: Observable<AdvancedReportFormStateModel>;

    @Select(AdvancedReportFormState.data)
    data$: Observable<Partial<AdvancedReport>>;

    // Data Controls
    labelControl = new FormControl(null, [Validators.maxLength(64), Validators.required]);
    descriptionControl = new FormControl(null, [Validators.maxLength(255)]);

    // Filter Controls
    orgKeyControl = new FormControl(null, [Validators.required]);
    cultivarsControl = new FormControl([], [Validators.required]);
    sitesControl = new FormControl([], [Validators.required]);
    sampleTypesControl = new FormControl([], [Validators.required]);
    cropsControl = new FormControl([], [Validators.required]);

    // Range Controls
    birthYearControl = new FormControl([], [Validators.required]);
    birthWeekControl = new FormControl([], [Validators.required]);

    // TODO refine filter controls their requirements
    filterFormGroup: FormGroup = new FormGroup({
        ownerOrgKey: this.orgKeyControl,
        scionCultivars: this.cultivarsControl,
        sites: this.sitesControl,
        sampleType: this.sampleTypesControl,
        scionCropId: this.cropsControl,
        birthYear: this.birthYearControl,
        birthWeek: this.birthWeekControl,
    });

    formGroup: FormGroup = new FormGroup({
        label: this.labelControl,
        description: this.descriptionControl,
    });

    private _destroy$ = new Subject();

    constructor(
        private _store: Store,
        private _dialogRef: MatDialogRef<AdvancedReportFormDialog>,
        private _snackbar: Snackbar,
        @Inject(MAT_DIALOG_DATA) public data: ReportFormDialogData,
    ) {}

    ngOnInit() {
        this._store.dispatch(new InitNewReportForm(this.data.key, this.data.defaults));

        this.state$.subscribe(state => {
            if (state.status === Status.COMPLETE) {
                this._dialogRef.close(state.data);
            } else if (state.status !== Status.LOADING) {
                this.formGroup.enable();
            } else {
                this.formGroup.disable();
            }
        });

        this.data$.pipe(takeUntil(this._destroy$))
            .subscribe(data => {
                if (data) this.reset(data);
            });
    }

    reset(model: Partial<AdvancedReport>) {
        let data = {
            label: model.label,
            description: model.description,
        };

        this.formGroup.reset(data);
    }

    attempt() {
        this.formGroup.updateValueAndValidity();
        this.formGroup.markAsDirty();

        if (!this.formGroup.valid) return this._snackbar.error("Invalid input. Check your input and try again");

        const form = this.formGroup.value;

        const report: Partial<AdvancedReport> = {
            ...this.data.defaults,
            label: form.label,
            description: form.description,
        };

        this._store.dispatch(new SubmitReportForm(report));
    }

    cancel() {
        this._dialogRef.close();
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
}